import { Controller } from "@hotwired/stimulus"
import { debounce } from "../helpers/timing_helpers"

export default class extends Controller {
  static targets = ["view", "form", "saveIndicator", "trixEditor"]
  static values = { 
    autoSave: { type: Boolean, default: true },
    debounceDelay: { type: Number, default: 800 }
  }

  connect() {
    // Check if we're in full edit mode (parent has task-edit controller)
    const taskEditController = this.element.closest('[data-controller*="task-edit"]')
    if (taskEditController) {
      this.autoSaveValue = false // Disable auto-save when in full edit mode
    }
    
    // Check if form has explicit auto-save setting
    if (this.hasFormTarget) {
      const form = this.formTarget.querySelector("form")
      if (form) {
        const autoSaveAttr = form.dataset.inlineEditAutoSaveValue
        if (autoSaveAttr === "false") {
          this.autoSaveValue = false
        }
        
        // Add submit listener with capture to sync Trix before Turbo intercepts
        form.addEventListener("submit", (event) => {
          this.syncTrixBeforeSubmit(event.target)
        }, { capture: true })
      }
    }
    
    // Store original value for cancel
    if (this.hasFormTarget) {
      const input = this.getInputField()
      if (input) {
        this.originalValue = input.value
        this.currentValue = input.value
        
        // Set up debounced auto-save for text inputs (but not Trix editors unless explicitly enabled)
        // Trix editors with auto-save disabled will use explicit Save button
        if (this.autoSaveValue && (input.tagName === "INPUT" || input.tagName === "TEXTAREA") && !this.hasTrixEditorTarget) {
          this.debouncedSave = debounce(() => this.autoSave(), this.debounceDelayValue)
        }
      }
    }
  }
  
  syncTrixBeforeSubmit(form) {
    // Sync Trix editor content to hidden input before form submission
    if (!this.hasTrixEditorTarget) {
      return false
    }
    
    const trixEditor = this.trixEditorTarget
    
    // Wait for Trix to be ready if needed
    if (!trixEditor || !trixEditor.editor) {
      console.error("[InlineEdit] Trix editor not initialized", { 
        hasTrixEditor: !!trixEditor,
        hasEditor: trixEditor ? !!trixEditor.editor : false
      })
      return false
    }
    
    const input = this.getInputField()
    if (!input) {
      console.error("[InlineEdit] Hidden input not found", {
        trixInputId: trixEditor.getAttribute("input"),
        form: form ? form.outerHTML.substring(0, 200) : "no form"
      })
      return false
    }
    
    // Get the HTML content from Trix editor
    // Trix normally syncs to the hidden input on form submit, but we're intercepting
    // So we need to manually serialize the editor's document to HTML
    let trixContent = ""
    
    // Method 1: Try to read from Trix's input property (if available)
    if (trixEditor.input) {
      trixContent = trixEditor.input.value || ""
    } else {
      
      // Since input property is not available, manually serialize the editor's document
      // Trix stores content in editor.composition.document
      // We need to serialize this to HTML
      if (trixEditor.editor) {
        try {
          const editor = trixEditor.editor
          
          // Trix editor maintains the HTML in the editor element
          // The editor's innerHTML contains the HTML representation
          // But it's wrapped in Trix's structure, so we need to extract the content
          
          // Actually, Trix syncs the document to HTML automatically
          // The editor element contains the HTML, but we need the actual content HTML
          // Trix maintains the HTML in editor.composition.document
          // We can serialize it by accessing the editor's HTML content
          
          // The simplest way: Trix editor's innerHTML contains the HTML
          // But it's wrapped, so we need to extract just the content
          // Actually, Trix stores the content HTML in the editor element
          // We can get it by reading the editor's content directly
          
          // Let's try reading the editor element's HTML
          // Trix maintains the HTML in the editor element
          const editorHTML = trixEditor.innerHTML
          
          // The editor's HTML contains the content, but wrapped in Trix structure
          // We need to extract just the content HTML
          // Trix wraps content in a div with class "trix-content"
          const contentDiv = trixEditor.querySelector(".trix-content")
          if (contentDiv) {
            trixContent = contentDiv.innerHTML || ""
          } else {
            // Fallback: use the editor's innerHTML directly
            // Trix might store the HTML differently
            trixContent = editorHTML || ""
          }
        } catch (error) {
          console.error("[InlineEdit] Error serializing Trix content:", error)
        }
      }
    }
    
    // Method 2: Fallback - read from hidden input (but this might be stale)
    if (!trixContent && input) {
      trixContent = input.value || ""
    }
    
    if (!trixContent) {
      // Still set empty string to ensure form submits
      input.value = ""
      return true
    }
    
    // Set the value directly on the hidden input
    // This is critical - we must set it before form submission
    input.value = trixContent
    
    // Also ensure Trix's internal input reference is updated
    if (trixEditor.input) {
      trixEditor.input.value = trixContent
    }
    
    // Trigger input and change events to ensure the value is registered
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
    
    // Verify the value was set
    if (input.value !== trixContent) {
      console.error("[InlineEdit] Failed to sync Trix content", { 
        trixContent: trixContent,
        inputValue: input.value,
        inputId: input.id,
        inputName: input.name
      })
      return false
    }
    
    return true
  }

  getInputField() {
    // For Trix editor, get the hidden input field
    if (this.hasTrixEditorTarget) {
      const trixInputId = this.trixEditorTarget.getAttribute("input")
      if (trixInputId) {
        // Try to find by ID first
        let input = document.getElementById(trixInputId)
        if (!input) {
          // If not found by ID, try to find by name within the form
          const form = this.formTarget.querySelector("form")
          if (form) {
            input = form.querySelector(`input[name="task[description]"]`)
          }
        }
        return input
      }
    }
    // Otherwise, get the first input, textarea, or select
    return this.formTarget.querySelector("input, textarea, select")
  }

  edit(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.hasViewTarget) {
      this.viewTarget.style.display = "none"
    }
    if (this.hasFormTarget) {
      this.formTarget.style.display = "block"
      
      // Focus Trix editor if present, otherwise focus the first input
      if (this.hasTrixEditorTarget) {
        // Ensure Trix editor is loaded with the current value
        const input = this.getInputField()
        const originalValue = input ? input.value : ""
        
        if (input && input.value) {
          this.trixEditorTarget.editor.loadHTML(input.value)
        } else {
          // Initialize with empty content if no value
          this.trixEditorTarget.editor.loadHTML("")
        }
        // Store initial value for cancel
        this.originalValue = originalValue
        this.currentValue = originalValue
        
        setTimeout(() => {
          this.trixEditorTarget.focus()
          
          // Select all content so user can immediately start typing to replace it
          if (this.trixEditorTarget.editor) {
            try {
              const editor = this.trixEditorTarget.editor
              const document = editor.getDocument()
              if (document) {
                // Get the length of the document using toString()
                const textContent = document.toString()
                const length = textContent.length
                
                if (length > 0) {
                  // Select all content in the editor
                  // Trix uses [start, end] range where end is exclusive
                  editor.setSelectedRange([0, length])
                  
                  // Verify selection worked by checking if range is set
                  const selection = editor.getSelectedRange()
                  
                  if (selection && (selection[0] !== 0 || selection[1] !== length)) {
                    // Selection didn't work, try keyboard shortcut approach
                    // Simulate Cmd+A (Mac) or Ctrl+A (Windows/Linux)
                    const selectAllEvent = new KeyboardEvent("keydown", {
                      key: "a",
                      code: "KeyA",
                      ctrlKey: true, // Ctrl on Windows/Linux, Cmd on Mac
                      metaKey: true, // Cmd on Mac
                      bubbles: true,
                      cancelable: true
                    })
                    this.trixEditorTarget.dispatchEvent(selectAllEvent)
                  }
                }
              }
            } catch (error) {
              console.error("[InlineEdit] Error selecting content:", error)
              // If selection fails, try keyboard shortcut to select all
              try {
                const selectAllEvent = new KeyboardEvent("keydown", {
                  key: "a",
                  code: "KeyA",
                  ctrlKey: true,
                  metaKey: true,
                  bubbles: true,
                  cancelable: true
                })
                this.trixEditorTarget.dispatchEvent(selectAllEvent)
              } catch (e) {
                console.warn("[InlineEdit] Could not select all content in Trix editor", error, e)
              }
            }
          }
        }, 150)
      } else {
        const input = this.formTarget.querySelector("input, textarea, select")
        if (input && input.type !== "hidden") {
          setTimeout(() => input.focus(), 0)
          if (input.select) {
            input.select()
          }
        }
      }
    }
  }

  cancel(event) {
    event.preventDefault()
    event.stopPropagation()
    
    if (this.hasFormTarget) {
      // Restore original value
      const input = this.getInputField()
      if (input && this.originalValue !== undefined) {
        input.value = this.originalValue
        this.currentValue = this.originalValue
        
        // If Trix editor, update its content
        if (this.hasTrixEditorTarget) {
          this.trixEditorTarget.editor.loadHTML(this.originalValue || "")
        }
      }
      
      // Cancel any pending saves
      if (this.debouncedSave) {
        clearTimeout(this.debouncedSave)
      }
      
      this.hideSaveIndicator()
      this.formTarget.style.display = "none"
    }
    if (this.hasViewTarget) {
      this.viewTarget.style.display = "block"
    }
  }

  handleInput(event) {
    // For text inputs, trigger debounced auto-save
    if (this.autoSaveValue && this.debouncedSave) {
      const input = event.target
      const newValue = input.value
      
      // Only save if value actually changed
      if (newValue !== this.currentValue) {
        this.currentValue = newValue
        this.showSaveIndicator("Saving...")
        this.debouncedSave()
      }
    }
  }

  handleTrixChange(event) {
    // Handle Trix editor changes - only auto-save if enabled
    // For description fields, we use explicit Save button instead
    if (this.autoSaveValue && this.debouncedSave) {
      const input = this.getInputField()
      if (input) {
        const newValue = input.value
        
        // Only save if value actually changed
        if (newValue !== this.currentValue) {
          this.currentValue = newValue
          this.showSaveIndicator("Saving...")
          this.debouncedSave()
        }
      }
    }
  }

  submitForm(event) {
    // Handle explicit form submission (Save button)
    event.preventDefault()
    event.stopPropagation()
    
    const form = this.formTarget.querySelector("form")
    if (!form) return
    
    // Sync Trix content before submission - do this synchronously
    const syncSuccess = this.syncTrixBeforeSubmit(form)
    
    // Verify the value is set before submitting
    const input = this.getInputField()
    const formData = new FormData(form)
    const descriptionValue = formData.get("task[description]")
    
    // If description is still empty after sync, log error and retry
    if (!descriptionValue || descriptionValue === "") {
      let trixContent = ""
      if (this.hasTrixEditorTarget) {
        const trixEditor = this.trixEditorTarget
        // Read from Trix's input reference or the hidden input
        if (trixEditor.input && trixEditor.input.value) {
          trixContent = trixEditor.input.value
        } else if (input && input.value) {
          trixContent = input.value
        }
      }
      
      console.error("Description is empty after sync attempt", {
        inputValue: input?.value,
        trixContent: trixContent,
        syncSuccess: syncSuccess,
        inputId: input?.id,
        inputName: input?.name
      })
      
      // Try one more sync attempt
      if (trixContent && input) {
        input.value = trixContent
      }
    }
    
    // Use Turbo's built-in form submission
    const handleSubmit = (submitEvent) => {
      if (submitEvent.detail.success) {
        const input = this.getInputField()
        if (input) {
          this.originalValue = input.value
          this.currentValue = input.value
        }
        
        this.closeEditMode()
      } else {
        // Error handling - could show error message
        console.error("Failed to save description")
      }
      
      form.removeEventListener("turbo:submit-end", handleSubmit)
    }
    
    form.addEventListener("turbo:submit-end", handleSubmit)
    // Use requestSubmit for Turbo forms - Trix should have synced by now
    form.requestSubmit()
  }

  handleBlur(event) {
    // Auto-save on blur for text inputs (immediate save if changed)
    if (this.autoSaveValue) {
      const input = event.target
      if ((input.tagName === "INPUT" || input.tagName === "TEXTAREA") && input.value !== this.originalValue) {
        this.autoSave()
      } else {
        // If value unchanged, just close
        this.closeEditMode()
      }
    } else {
      // If auto-save disabled, close on blur
      this.closeEditMode()
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.cancel(event)
    } else if (event.key === "Enter" && event.target.tagName !== "TEXTAREA" && !event.shiftKey) {
      // For Enter key, save immediately and close
      if (this.autoSaveValue) {
        event.preventDefault()
        this.autoSave()
      } else {
        event.preventDefault()
        const form = this.formTarget.querySelector("form")
        if (form) {
          form.requestSubmit()
        }
      }
    }
  }

  submitOnChange(event) {
    // Auto-submit on change for select fields (immediate)
    const form = event.target.closest("form")
    if (form) {
      this.showSaveIndicator("Saving...")
      form.requestSubmit()
    }
  }

  handleDateChange(event) {
    // Auto-save when date is selected from datepicker
    if (this.autoSaveValue) {
      const input = event.target
      if (input.value && input.value !== this.originalValue) {
        this.currentValue = input.value
        this.autoSave()
      }
    }
  }

  autoSave() {
    const form = this.formTarget.querySelector("form")
    if (!form) return

    this.showSaveIndicator("Saving...")
    
    // Use Turbo's built-in form submission which handles streams automatically
    // Add a listener for successful submission
    const handleSubmit = (event) => {
      if (event.detail.success) {
        this.showSaveIndicator("Saved", true)
        this.originalValue = this.currentValue
        
        // Update current value from the input
        const input = this.getInputField()
        if (input) {
          this.currentValue = input.value
        }
        
        setTimeout(() => {
          this.hideSaveIndicator()
          this.closeEditMode()
        }, 1000)
      } else {
        this.showSaveIndicator("Error saving", false)
        setTimeout(() => this.hideSaveIndicator(), 2000)
      }
      
      form.removeEventListener("turbo:submit-end", handleSubmit)
    }
    
    form.addEventListener("turbo:submit-end", handleSubmit)
    form.requestSubmit()
  }

  closeEditMode() {
    if (this.hasFormTarget) {
      this.formTarget.style.display = "none"
    }
    if (this.hasViewTarget) {
      this.viewTarget.style.display = "block"
    }
  }

  showSaveIndicator(text, success = null) {
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.textContent = text
      this.saveIndicatorTarget.style.display = "inline-block"
      this.saveIndicatorTarget.className = success === true ? "save-indicator success" : 
                                           success === false ? "save-indicator error" : 
                                           "save-indicator"
    }
  }

  hideSaveIndicator() {
    if (this.hasSaveIndicatorTarget) {
      this.saveIndicatorTarget.style.display = "none"
    }
  }
}

